import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  IconButton,
  HStack,
  Text,
  Spinner,
  Center,
  Badge,
  Flex,
  Tooltip,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Textarea,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Toast,
  useToast,
} from '@chakra-ui/react';
import {
  EditIcon,
  CheckIcon,
  CloseIcon,
  InfoIcon,
  RepeatIcon,
  ChevronDownIcon,
  WarningIcon,
} from '@chakra-ui/icons';
import { FiClipboard } from 'react-icons/fi';
import excelModalApi from '../../../../services/excelModalApi';

// ─── Helper: detect if a column is numeric based on its existing row values ───
const isNumericColumn = (rows, colIndex) => {
  if (!rows || rows.length === 0) return false;
  // Check up to 10 non-empty rows to decide
  let checkedCount = 0;
  let numericCount = 0;
  for (let i = 0; i < rows.length && checkedCount < 10; i++) {
    const val = rows[i]?.[colIndex];
    if (val !== null && val !== undefined && val !== '') {
      checkedCount++;
      if (!isNaN(Number(val)) && String(val).trim() !== '') {
        numericCount++;
      }
    }
  }
  // If majority of sampled non-empty cells are numeric, treat as numeric column
  return checkedCount > 0 && numericCount / checkedCount >= 0.8;
};

// ─── Helper: check if a key press is valid for a numeric input ────────────────
// Allowed: digits 0-9, decimal point, minus sign (only at start), backspace,
// delete, arrows, tab, home, end, ctrl+a/c/v/x
const isValidNumericKey = (e) => {
  const allowedKeys = [
    'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
    'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End', 'Enter',
  ];
  if (allowedKeys.includes(e.key)) return true;
  if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x', 'z'].includes(e.key.toLowerCase())) return true;
  if (e.key === '-' && e.target.selectionStart === 0 && !e.target.value.includes('-')) return true;
  if (e.key === '.' && !e.target.value.includes('.')) return true;
  if (/^\d$/.test(e.key)) return true;
  return false;
};
// ─────────────────────────────────────────────────────────────────────────────

const ExcelGrid = ({
  headers,
  rows,
  onCellEdit,
  isLoading,
  hasUnsavedChanges,
  onSave,
  onDiscard,
  isSaving,
  successColor,
  warningColor,
  dangerColor,
  infoColor,
  borderColor,
  isEditMode,
  onStartEdit,
  onCancelEdit,
  editingFileId,
  files,
  fileRowRanges,
  isFileEditing,
  isAnyFileEditing,
  isCellEdited,
  fileStatuses,
  scenario,
  fileInfo,
  onApproveFileEdit,
  fileType
}) => {
  const [editingCell, setEditingCell] = React.useState(null);
  const [targetVal, setTargetVal] = React.useState('');
  const toast = useToast();
  const [isTargetSaving, setIsTargetSaving] = React.useState(false);
  const currentFY = "2026";
  const [approveEditModal, setApproveEditModal] = React.useState({
    isOpen: false,
    fileId: null,
    fileName: '',
  });

  // ─── Track invalid key attempt per cell for tooltip display ─────────────────
  const [invalidInputCell, setInvalidInputCell] = React.useState(null);
  // ─────────────────────────────────────────────────────────────────────────────

  console.log(fileType, "-----------------files--------------")
  // Paste modal state
  const { isOpen: isPasteModalOpen, onOpen: onPasteModalOpen, onClose: onPasteModalClose } = useDisclosure();
  const [selectedColumnForPaste, setSelectedColumnForPaste] = React.useState(null);
  const [pasteData, setPasteData] = React.useState('');

  // ─── Memoize numeric column detection (once per render when rows change) ────
  const numericColumnMap = React.useMemo(() => {
    if (!headers || !rows) return {};
    const map = {};
    headers.forEach((_, colIndex) => {
      map[colIndex] = isNumericColumn(rows, colIndex);
    });
    return map;
  }, [headers, rows]);
  // ─────────────────────────────────────────────────────────────────────────────

  // const handleUpdateTarget = async () => {
  //   if (!targetVal.trim()) {
  //     toast({
  //       title: 'Empty value',
  //       description: 'Please enter a target value first',
  //       status: 'warning',
  //       duration: 3000,
  //       position: 'top-right',
  //     });
  //     return;
  //   }

  //   setIsTargetSaving(true);
  //   try {
  //     const result = await excelModalApi.updateTargetRecovery(currentFY, targetVal, fileType?._id);
  //     if (result.success) {
  //       toast({
  //         title: 'Success',
  //         description: result.message || 'Target updated successfully',
  //         status: 'success',
  //         duration: 3000,
  //         position: 'top-right',
  //       });
  //       setTargetVal('');
  //     } else {
  //       toast({
  //         title: 'Error updating target',
  //         description: result.error,
  //         status: 'error',
  //         duration: 3000,
  //         position: 'top-right',
  //       });
  //     }
  //   } catch (error) {
  //     toast({
  //       title: 'Error',
  //       description: error.message,
  //       status: 'error',
  //       duration: 3000,
  //       position: 'top-right',
  //     });
  //   } finally {
  //     setIsTargetSaving(false);
  //   }
  // };

  const tableContainerRef = React.useRef(null);

  // ─── Compute total edited cells count ───────────────────────────────────────
  const totalEditedCount = React.useMemo(() => {
    if (!rows || !headers) return 0;
    let count = 0;
    for (let r = 0; r < rows.length; r++) {
      for (let c = 0; c < (rows[r]?.length ?? 0); c++) {
        if (isCellEdited(r, c)) count++;
      }
    }
    return count;
  }, [rows, headers, isCellEdited]);
  // ────────────────────────────────────────────────────────────────────────────

  // Define non-editable headers
  const nonEditableHeaders = [
    'Current Financial Year (आर्थिक वर्ष)',
    'Month (महिना)',
    'District (जिल्हा)',
    'Sub-Division (उपविभाग)',
    'Taluka (तालुका)',
    'Circle (मंडळ)',
    'Village (गाव)',
    'Current Financial Year (चालू आर्थिक वर्ष)',
    'Previous Financial Year (मागील आर्थिक वर्ष) (System)',
    'Current Financial Year (चालू आर्थिक वर्ष) (System)',
    'Taluka (तालुके)',
    'Godown (गोदाम)',
    "Division (विभाग)"
  ];

  // Helper function to check if a column is editable
  const isColumnEditable = (colIndex) => {
    if (headers && headers[colIndex]) {
      return !nonEditableHeaders.includes(headers[colIndex]);
    }
    return true;
  };

  
  const handlePasteToColumn = (colIndex) => {
    if (!isColumnEditable(colIndex)) {
      alert("This column is not editable!");
      return;
    }
    setSelectedColumnForPaste(colIndex);
    setPasteData('');
    onPasteModalOpen();
  };

  // Process paste data
  const processPasteData = () => {
    if (!pasteData.trim()) {
      alert("Please paste data into the textarea");
      return;
    }

    const colIndex = selectedColumnForPaste;
    if (colIndex === null) return;

    // Split by newline and clean up
    let pastedValues = pasteData.split(/\r?\n/).filter(line => line.trim() !== '');

    if (pastedValues.length === 0) {
      alert("No valid data to paste");
      return;
    }

    let successCount = 0;
    let skippedCount = 0;

    // Update each row
    pastedValues.forEach((value, index) => {
      if (index < rows.length) {
        const currentValue = rows[index][colIndex];
        if (value !== currentValue) {
          onCellEdit(index, colIndex, value);
          successCount++;
        }
      } else {
        skippedCount++;
      }
    });

    // Show result
    Toast(`Successfully pasted ${successCount} values${skippedCount > 0 ? `, ${skippedCount} rows skipped (beyond data range)` : ''}`);

    // Close modal and reset
    onPasteModalClose();
    setSelectedColumnForPaste(null);
    setPasteData('');
  };

  const isNonTaluka =
    scenario === 'nonTalukaYearly' || scenario === 'nonTalukaMonthly';

  const getNonTalukaFileStatus = () => {
    if (files && files.length > 0) {
      return files[0]?.status || 'submitted';
    }
    return fileStatuses?.[editingFileId] || 'submitted';
  };

  const getFileUploadInfo = () => {
    if (files && files.length > 0) {
      const file = files[0];
      return {
        uploadedBy: file.uploadedBy?.name || 'Unknown',
        uploadedDate: file.uploadedDate
          ? new Date(file.uploadedDate).toLocaleString()
          : 'Unknown',
        fileName: file.nameOfOffice || file.name || 'Unknown',
      };
    }
    return null;
  };

  const handleEditClick = (fileId, fileStatus, fileName = '') => {
    if (fileStatus === 'approved') {
      setApproveEditModal({
        isOpen: true,
        fileId,
        fileName,
      });
    } else {
      onStartEdit(fileId);
    }
  };

  const handleApproveEditConfirm = () => {
    setApproveEditModal({ isOpen: false, fileId: null, fileName: '' });
    if (onApproveFileEdit) {
      onApproveFileEdit(approveEditModal.fileId);
    }
    onStartEdit(approveEditModal.fileId);
  };

  const handleApproveEditCancel = () => {
    setApproveEditModal({ isOpen: false, fileId: null, fileName: '' });
  };

  if (isLoading) {
    return (
      <Center h="100%" bg="white">
        <Box textAlign="center">
          <Spinner size="xl" color={infoColor} thickness="4px" mb={4} />
          <Text color="gray.500">Loading Excel data...</Text>
        </Box>
      </Center>
    );
  }

  if (!headers || headers.length === 0 || !rows || rows.length === 0) {
    return (
      <Center h="100%" bg="white" flexDirection="column">
        <Box
          w="100px"
          h="100px"
          borderRadius="full"
          bg="gray.100"
          display="flex"
          alignItems="center"
          justifyContent="center"
          mb={4}
        >
          <Text fontSize="40px" color="gray.400">
            📊
          </Text>
        </Box>
        <Text fontSize="lg" color="gray.500" mb={2}>
          No data to display
        </Text>
        <Text fontSize="sm" color="gray.400">
          {isNonTaluka
            ? 'No file uploaded for this period'
            : 'Select talukas to view Excel data'}
        </Text>
      </Center>
    );
  }

  
  const getFileInfoForRow = (rowIndex) => {
    if (isNonTaluka || !fileRowRanges || fileRowRanges.length === 0)
      return null;
    return fileRowRanges.find(
      (range) => rowIndex >= range.startRow && rowIndex <= range.endRow,
    );
  };

  const isRowInEditingFile = (rowIndex) => {
    if (isNonTaluka) return isEditMode;
    const fileInfo = getFileInfoForRow(rowIndex);
    return fileInfo && fileInfo.fileId === editingFileId;
  };

  
  const getFileStatusForRow = (rowIndex) => {
    if (isNonTaluka) return getNonTalukaFileStatus();
    const fileInfo = getFileInfoForRow(rowIndex);
    if (!fileInfo) return 'submitted';
    return fileStatuses?.[fileInfo.fileId] || fileInfo.status || 'submitted';
  };


  
  const getFileBackgroundColor = (fileIndex, isInEditingFile, fileStatus) => {
    if (isInEditingFile) return 'blue.100';
    if (fileStatus === 'rejected') return 'red.100';
    if (fileStatus === 'approved') return 'green.100';
    if (isNonTaluka) return fileIndex % 2 === 0 ? 'white' : 'gray.50';
    return fileIndex % 2 === 0 ? 'white' : 'gray.50';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'green';
      case 'rejected':
        return 'red';
      case 'submitted':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const gradientHeaderBg = 'linear-gradient(135deg, #3b7669 0%, #1b7e7e 100%)';
  const headerTextColor = 'white';
  const fileUploadInfo = getFileUploadInfo();
  const nonTalukaStatus = getNonTalukaFileStatus();

  return (
    <Box h="100%" display="flex" flexDirection="column" bg="white">
      {/* Paste Modal */}
      <Modal isOpen={isPasteModalOpen} onClose={onPasteModalClose} size="xl" isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader bg="teal.600" color="white" borderTopRadius="xl">
            <Flex align="center">
              <FiClipboard style={{ marginRight: '8px' }} />
              Paste Data into {selectedColumnForPaste !== null && headers[selectedColumnForPaste]}
            </Flex>
          </ModalHeader>
          <ModalCloseButton color="white" />

          <ModalBody py={6}>
            <Text mb={3} fontSize="sm" color="gray.600">
              Paste your data from Excel, Google Sheets, or any other source below.
              Each line will be pasted into a separate row in this column.
            </Text>

            <Textarea
              placeholder="Paste your data here...
Example:
Value 1
Value 2
Value 3
Value 4"
              value={pasteData}
              onChange={(e) => setPasteData(e.target.value)}
              rows={10}
              fontFamily="monospace"
              fontSize="sm"
              borderColor="teal.300"
              _focus={{ borderColor: "teal.500", boxShadow: "0 0 0 1px teal.500" }}
            />

            <Alert mt={4} status="info" borderRadius="md" size="sm" variant="left-accent">
              <AlertIcon />
              <Box fontSize="sm">
                <Text fontWeight="bold">Tips:</Text>
                <Text>• Each line becomes one cell value</Text>
                <Text>• For Excel data, only the first column will be used</Text>
                <Text>• Empty lines will be ignored</Text>
                <Text>• You can paste {rows.length} rows maximum</Text>
              </Box>
            </Alert>
          </ModalBody>

          <ModalFooter borderTop="1px" borderColor="gray.200">
            <Button variant="ghost" mr={3} onClick={onPasteModalClose}>
              Cancel
            </Button>
            <Button colorScheme="teal" onClick={processPasteData} leftIcon={<FiClipboard />}>
              Paste Data
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Approved File Edit Modal */}
      <Modal
        isOpen={approveEditModal.isOpen}
        onClose={handleApproveEditCancel}
        size="lg"
        isCentered
      >
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader color="orange.600" borderBottom="1px" borderColor="gray.200">
            <Flex align="center">
              <InfoIcon color="orange.500" mr={2} />
              Edit Approved File
            </Flex>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody py={6}>
            <Alert status="warning" variant="left-accent" borderRadius="lg" mb={4}>
              <AlertIcon />
              <Box flex="1">
                <AlertTitle fontSize="md">You are editing an approved file</AlertTitle>
                <AlertDescription fontSize="sm">
                  This file has already been approved. Editing it will change its status
                  back to "submitted" and it will need to be approved again.
                </AlertDescription>
              </Box>
            </Alert>

            <Text fontSize="sm" color="gray.600" mt={3}>
              File: <strong>{approveEditModal.fileName || 'Selected file'}</strong>
            </Text>

            <Text fontSize="sm" color="gray.600" mt={2}>
              Are you sure you want to proceed with editing?
            </Text>
          </ModalBody>

          <ModalFooter borderTop="1px" borderColor="gray.200">
            <Button variant="ghost" mr={3} onClick={handleApproveEditCancel} isDisabled={isSaving}>
              Cancel
            </Button>
            <Button colorScheme="orange" onClick={handleApproveEditConfirm} isLoading={isSaving} leftIcon={<EditIcon />}>
              Yes, Edit File
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Mode Bar */}
      {isEditMode && (
        <Flex
          p={2}
          bg="blue.50"
          borderBottom="2px"
          borderColor="blue.200"
          align="center"
          justify="space-between"
        >
          <HStack spacing={2}>
            <Badge colorScheme="blue" variant="solid" px={2} py={0.5} fontSize="xs">
              EDIT MODE
            </Badge>
            <Text fontSize="sm" color="blue.800">
              {isNonTaluka
                ? 'Editing this Data'
                : 'Editing one Data. Other Data are locked.'}
            </Text>
          </HStack>

          <HStack spacing={2}>
            {hasUnsavedChanges ? (
              <>
                <Button size="xs" colorScheme="green" leftIcon={<CheckIcon />} onClick={onSave} isLoading={isSaving}>
                  Save
                </Button>
                <Button size="xs" colorScheme="red" variant="outline" leftIcon={<CloseIcon />} onClick={onDiscard} isDisabled={isSaving}>
                  Discard
                </Button>
              </>
            ) : (
              <Button size="xs" onClick={onCancelEdit}>
                Exit Edit Mode
              </Button>
            )}
          </HStack>
        </Flex>
      )}

      {isNonTaluka && !isEditMode && fileUploadInfo && (
        <Flex
          p={3}
          bg="gray.50"
          borderBottom="1px"
          borderColor={borderColor}
          align="center"
          justify="space-between"
        >
          <HStack spacing={4}>
            <Badge colorScheme={getStatusColor(nonTalukaStatus)} px={3} py={1} borderRadius="full">
              Status: {nonTalukaStatus}
            </Badge>
            <Tooltip label={`Uploaded on: ${fileUploadInfo.uploadedDate}`}>
              <Badge colorScheme="teal" variant="outline" px={3} py={1} borderRadius="full">
                📅 {new Date(fileUploadInfo.uploadedDate).toLocaleDateString()}
              </Badge>
            </Tooltip>
          </HStack>
        </Flex>
      )}

      {!isNonTaluka && files?.length > 1 && (
        <Flex p={2} bg="gray.100" borderBottom="1px" borderColor={borderColor} justify="space-between" align="center">
          {hasUnsavedChanges && (
            <Badge colorScheme="green" variant="solid" px={2} py={0.5} fontSize="xs">
              Unsaved Changes
            </Badge>
          )}
        </Flex>
      )}

      {/* Target Input Box */}
      {/* {files[0]?.scheme?._id === "68b9513ffa74b7a86b7aa38a" && (
        <Flex
          p={2}
          px={4}
          bg="blue.50"
          borderBottom="1px solid"
          borderColor="blue.200"
          align="center"
          gap={3}
        >
          <Text fontSize="sm" fontWeight="bold" color="blue.800">
            Target:
          </Text>
          <Input
            placeholder="Enter target value"
            size="sm"
            maxW="200px"
            borderRadius="md"
            bg="white"
            borderColor="blue.200"
            _hover={{ borderColor: 'blue.300' }}
            _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
            value={targetVal}
            onChange={(e) => setTargetVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleUpdateTarget(); }}
          />
          <Button size="sm" colorScheme="teal" isLoading={isTargetSaving} onClick={handleUpdateTarget} ml={2}>
            Apply Target
          </Button>
        </Flex>
      )} */}

      {/* Main Table */}
      <Box
        ref={tableContainerRef}
        flex={1}
        overflow="auto"
        position="relative"
        sx={{
          '&::-webkit-scrollbar': {
            height: '12px',
            width: '12px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'gray.100',
            borderRadius: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'gray.400',
            borderRadius: '6px',
            '&:hover': {
              background: 'gray.500',
            },
          },
        }}
      >
        <Box minWidth="100%" display="inline-block">
          <Table variant="simple" size="sm">
            <Thead position="sticky" top={0} zIndex={10}>
              <Tr bgGradient={gradientHeaderBg}>
                <Th
                  position="sticky"
                  left={0}
                  bgGradient={gradientHeaderBg}
                  color={headerTextColor}
                  zIndex={20}
                  width="60px"
                  minWidth="60px"
                  borderRight="2px"
                  borderColor="rgba(255,255,255,0.2)"
                  boxShadow="2px 0 5px -2px rgba(0,0,0,0.1)"
                  fontSize="sm"
                  fontWeight="600"
                  textTransform="uppercase"
                  letterSpacing="0.5px"
                >
                  SR.NO
                </Th>
                {headers.map((header, idx) => {
                  const isEditable = isColumnEditable(idx);
                  const isNumeric = numericColumnMap[idx];
                  return (
                    <Th
                      key={idx}
                      minWidth="200px"
                      bgGradient={gradientHeaderBg}
                      color={headerTextColor}
                      fontSize="sm"
                      fontWeight="600"
                      textTransform="uppercase"
                      letterSpacing="0.5px"
                      borderRight="1px"
                      borderColor="rgba(255,255,255,0.2)"
                    >
                      <Flex align="center" justify="space-between" gap={2}>
                        <Tooltip label={header} placement="top">
                          <Text>{header}</Text>
                        </Tooltip>

                        {/* Copy/Paste buttons for editable columns */}
                        {isEditable && isEditMode && (
                          <HStack spacing={1}>
                            {/* Numeric column indicator badge */}
                            {/* {isNumeric && (
                              <Tooltip label="This column only accepts numbers" placement="top">
                                <Badge
                                  colorScheme="yellow"
                                  variant="solid"
                                  fontSize="9px"
                                  px={1}
                                  py={0.5}
                                  borderRadius="sm"
                                  cursor="default"
                                >
                                  #123
                                </Badge>
                              </Tooltip>
                            )} */}
                            <Menu>
                              <MenuButton
                                as={IconButton}
                                icon={<ChevronDownIcon />}
                                size="xs"
                                variant="ghost"
                                colorScheme="whiteAlpha"
                                aria-label="Column options"
                                _hover={{ bg: 'whiteAlpha.300' }}
                              />
                              <MenuList>
                                <MenuItem
                                  bg="blue.500"
                                  icon={<FiClipboard />}
                                  onClick={() => handlePasteToColumn(idx)}
                                >
                                  Paste to Column
                                </MenuItem>
                              </MenuList>
                            </Menu>
                          </HStack>
                        )}

                        {/* Show non-editable badge */}
                        {!isEditable && (
                          <Tooltip label="This column cannot be edited">
                            <Badge size="xs" colorScheme="gray" ml={1}>🔒</Badge>
                          </Tooltip>
                        )}
                      </Flex>
                    </Th>
                  );
                })}
                <Th
                  position="sticky"
                  right={0}
                  bgGradient={gradientHeaderBg}
                  color={headerTextColor}
                  zIndex={20}
                  width={isNonTaluka ? '80px' : '140px'}
                  minWidth={isNonTaluka ? '80px' : '140px'}
                  borderLeft="2px"
                  borderColor="rgba(255,255,255,0.2)"
                  boxShadow="-2px 0 5px -2px rgba(0,0,0,0.1)"
                  textAlign="center"
                  fontSize="sm"
                  fontWeight="600"
                  textTransform="uppercase"
                  letterSpacing="0.5px"
                >
                  {isNonTaluka ? 'Edit' : 'Actions'}
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row, rowIdx) => {
                const fileInfo = getFileInfoForRow(rowIdx);
                const fileIndex = !isNonTaluka
                  ? fileRowRanges.findIndex(
                    (r) => r.fileId === fileInfo?.fileId,
                  )
                  : 0;
                const fileStatus = getFileStatusForRow(rowIdx);
                const isInEditingFile = isNonTaluka
                  ? isEditMode
                  : fileInfo && fileInfo.fileId === editingFileId;
                const canEditRow = isEditMode && isInEditingFile;
                const isFirstRowOfFile = isNonTaluka
                  ? rowIdx === 0
                  : fileInfo && rowIdx === fileInfo.startRow;
                const isLastRowOfFile = isNonTaluka
                  ? rowIdx === rows.length - 1
                  : fileInfo && rowIdx === fileInfo.endRow;

                const bgColor = getFileBackgroundColor(
                  fileIndex,
                  isInEditingFile,
                  fileStatus,
                );

                return (
                  <React.Fragment key={rowIdx}>
                    {!isNonTaluka && isFirstRowOfFile && fileIndex > 0 && (
                      <Tr>
                        <Td colSpan={headers.length + 2} p={0} bg="transparent" border="none">
                          <Flex h="1px" bg="gray.300" w="100%" />
                        </Td>
                      </Tr>
                    )}

                    <Tr
                      bg={bgColor}
                      _hover={{ bg: canEditRow ? 'blue.100' : bgColor }}
                      transition="background 0.2s"
                      borderBottom={isLastRowOfFile ? '2px solid' : '1px solid'}
                      borderBottomColor={isLastRowOfFile ? 'gray.400' : borderColor}
                    >
                      <Td
                        position="sticky"
                        left={0}
                        bg={bgColor}
                        zIndex={5}
                        fontWeight="bold"
                        borderRight="2px"
                        borderColor={borderColor}
                        boxShadow="2px 0 5px -2px rgba(0,0,0,0.1)"
                        textAlign="center"
                      >
                        {rowIdx + 1}
                      </Td>

                      {row.map((cell, colIdx) => {
                        const isEditing =
                          editingCell?.row === rowIdx && editingCell?.col === colIdx;
                        const isEdited = isCellEdited(rowIdx, colIdx);
                        const columnEditable = isColumnEditable(colIdx);
                        const canEditCell = canEditRow && columnEditable;
                        const isNumeric = numericColumnMap[colIdx];

                        // ── Check if this cell is showing the invalid-input tooltip ──
                        const showInvalidTooltip =
                          invalidInputCell?.row === rowIdx &&
                          invalidInputCell?.col === colIdx;
                        // ─────────────────────────────────────────────────────────────

                        let cellBg = bgColor;
                        if (isEditing) {
                          cellBg = 'blue.100';
                        } else if (isEdited) {
                          cellBg = 'green.100';
                        } else if (!columnEditable && canEditRow) {
                          cellBg = 'gray.50';
                        }

                        return (
                          <Td
                            key={colIdx}
                            p={1}
                            borderRight="1px"
                            borderColor={borderColor}
                            bg={cellBg}
                            cursor={canEditCell ? 'text' : 'default'}
                            position="relative"
                          >
                            {isEditing && canEditCell ? (
                              // ─── Numeric validation tooltip wraps the Input ──────────
                              <Tooltip
                                label={
                                  isNumeric
                                    ? showInvalidTooltip
                                      ? '⚠️ Only numbers are allowed in this field'
                                      : 'Only numbers allowed'
                                    : undefined
                                }
                                isOpen={isNumeric ? (showInvalidTooltip || undefined) : false}
                                placement="top"
                                hasArrow
                                bg={showInvalidTooltip ? 'red.500' : 'gray.700'}
                                color="white"
                                fontSize="xs"
                                borderRadius="md"
                                px={2}
                                py={1}
                              >
                                <Input
                                  value={cell || ''}
                                  onChange={(e) => {
                                    onCellEdit(rowIdx, colIdx, e.target.value);
                                  }}
                                  onBlur={() => {
                                    setEditingCell(null);
                                    setInvalidInputCell(null);
                                  }}
                                  onKeyDown={(e) => {
                                    // ── Numeric column: block non-numeric keys ──────────
                                    if (isNumeric && !isValidNumericKey(e)) {
                                      e.preventDefault();
                                      // Show tooltip on the cell briefly
                                      setInvalidInputCell({ row: rowIdx, col: colIdx });
                                      // Auto-hide tooltip after 1.5s
                                      setTimeout(() => setInvalidInputCell(null), 1500);
                                      return;
                                    }
                                    // ───────────────────────────────────────────────────
                                    if (e.key === 'Enter') {
                                      setEditingCell(null);
                                      setInvalidInputCell(null);
                                    }
                                  }}
                                  onPaste={(e) => {
                                    // ── Validate pasted content for numeric columns ──────
                                    if (isNumeric) {
                                      const pasted = e.clipboardData.getData('text');
                                      if (pasted && isNaN(Number(pasted.trim()))) {
                                        e.preventDefault();
                                        setInvalidInputCell({ row: rowIdx, col: colIdx });
                                        setTimeout(() => setInvalidInputCell(null), 1500);
                                      }
                                    }
                                    // ───────────────────────────────────────────────────
                                  }}
                                  size="sm"
                                  autoFocus
                                  borderRadius="md"
                                  borderColor={showInvalidTooltip ? 'red.400' : 'blue.400'}
                                  bg={showInvalidTooltip ? 'red.50' : 'white'}
                                  _focus={{
                                    borderColor: showInvalidTooltip ? 'red.500' : 'blue.500',
                                    boxShadow: showInvalidTooltip
                                      ? '0 0 0 1px var(--chakra-colors-red-500)'
                                      : '0 0 0 1px blue.500',
                                  }}
                                  inputMode={isNumeric ? 'decimal' : 'text'}
                                />
                              </Tooltip>
                              // ────────────────────────────────────────────────────────────
                            ) : (
                              <Flex
                                align="center"
                                justify="space-between"
                                minH="32px"
                                px={2}
                                onClick={() => {
                                  if (canEditCell) {
                                    setEditingCell({ row: rowIdx, col: colIdx });
                                    setInvalidInputCell(null);
                                  }
                                }}
                                _hover={canEditCell ? { bg: 'blue.100' } : {}}
                                borderRadius="md"
                                cursor={canEditCell ? 'pointer' : 'default'}
                              >
                                <Text
                                  fontSize="sm"
                                  noOfLines={2}
                                  fontWeight={isEdited ? '500' : 'normal'}
                                  color={!columnEditable ? 'gray.500' : 'inherit'}
                                  fontStyle={!columnEditable ? 'italic' : 'normal'}
                                >
                                  {cell || (
                                    <Text as="span" color="gray.400">
                                      —
                                    </Text>
                                  )}
                                </Text>
                                {isEdited && (
                                  <Badge
                                    position="absolute"
                                    top="2px"
                                    right="2px"
                                    colorScheme="green"
                                    size="xs"
                                    borderRadius="full"
                                    w="8px"
                                    h="8px"
                                    p={0}
                                  />
                                )}
                                {!columnEditable && canEditRow && (
                                  <Tooltip label="This field cannot be edited">
                                    <Badge
                                      position="absolute"
                                      top="2px"
                                      right="2px"
                                      colorScheme="gray"
                                      size="xs"
                                      borderRadius="full"
                                      fontSize="10px"
                                      px={1}
                                    >
                                      🔒
                                    </Badge>
                                  </Tooltip>
                                )}
                              </Flex>
                            )}
                          </Td>
                        );
                      })}

                      <Td
                        position="sticky"
                        right={0}
                        bg={bgColor}
                        zIndex={5}
                        borderLeft="2px"
                        borderColor={borderColor}
                        boxShadow="-2px 0 5px -2px rgba(0,0,0,0.1)"
                        textAlign="center"
                        verticalAlign="middle"
                      >
                        {isNonTaluka
                          ? rowIdx === 0 && (
                            <Center w="100%">
                              {isEditMode ? (
                                <Badge colorScheme="blue" variant="solid" px={3} py={1.5} borderRadius="md">
                                  Editing
                                </Badge>
                              ) : (
                                <Tooltip label="Edit this file">
                                  <IconButton
                                    icon={<EditIcon />}
                                    size="sm"
                                    colorScheme="blue"
                                    onClick={() =>
                                      handleEditClick(
                                        editingFileId || files?.[0]?._id,
                                        nonTalukaStatus,
                                        files?.[0]?.nameOfOffice || files?.[0]?.name
                                      )
                                    }
                                    isDisabled={isAnyFileEditing()}
                                    borderRadius="full"
                                    boxShadow="sm"
                                    _hover={{ transform: 'scale(1.05)' }}
                                    transition="all 0.2s"
                                  />
                                </Tooltip>
                              )}
                            </Center>
                          )
                          : isFirstRowOfFile &&
                          fileInfo && (
                            <Flex direction="column" align="center" justify="center" minH="40px">
                              <Center w="100%">
                                {isFileEditing && isFileEditing(fileInfo.fileId) ? (
                                  <Badge colorScheme="blue" variant="solid" px={3} py={1.5} borderRadius="md">
                                    Editing
                                  </Badge>
                                ) : (
                                  <Tooltip
                                    label={
                                      isAnyFileEditing()
                                        ? 'Another file is being edited'
                                        : fileStatus === 'rejected'
                                          ? 'This Record is rejected. Edit to resubmit for approval'
                                          : fileStatus === 'approved'
                                            ? 'This Record is approved. Editing will change status to submitted'
                                            : 'Edit this Record'
                                    }
                                  >
                                    <IconButton
                                      icon={<EditIcon />}
                                      size="md"
                                      colorScheme={fileStatus === 'rejected' || fileStatus === 'approved' ? 'orange' : 'blue'}
                                      variant={isAnyFileEditing() ? 'outline' : 'solid'}
                                      onClick={() =>
                                        handleEditClick(fileInfo.fileId, fileStatus, fileInfo.fileName)
                                      }
                                      isDisabled={isAnyFileEditing()}
                                      opacity={isAnyFileEditing() ? 0.5 : 1}
                                      aria-label={`Edit file ${fileInfo.fileName || ''}`}
                                      borderRadius="full"
                                      boxShadow="sm"
                                      _hover={{ transform: 'scale(1.05)' }}
                                      transition="all 0.2s"
                                    />
                                  </Tooltip>
                                )}
                              </Center>
                            </Flex>
                          )}
                      </Td>
                    </Tr>
                  </React.Fragment>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
};

export default ExcelGrid;